import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type {
  PortalOutboundAreaSummary,
  PortalOutboundEntityType,
  PortalOutboundItemStatus,
  PortalOutboundPreview,
  PortalOutboundPreviewItem,
  PortalOutboundProductSnapshot,
} from '@/types/portalOutbound';

interface FloorPlanRow {
  id: string;
  name: string;
  project_id: string;
}

interface ProjectRow {
  id: string;
  name: string;
  client: string | null;
  location: string | null;
  description: string | null;
}

interface RoomRow {
  id: string;
  floor_plan_id: string;
  name: string;
}

interface ProductLibraryRow {
  id: string;
  brand: string | null;
  model: string | null;
  part_number: string | null;
  description: string | null;
  short_description: string | null;
  category: string | null;
  subcategory: string | null;
  price: number | null;
  sell_price: number | null;
  cost: number | null;
  portal_item_id: number | null;
  referenced_item_id: number | null;
  portal_item_identifier: string | null;
  source: string | null;
}

interface FixturePreviewRow {
  id: string;
  floor_plan_id: string;
  room_id: string | null;
  product_id: string | null;
  custom_name: string | null;
  fixture_type: string | null;
  category: string | null;
  subcategory: string | null;
}

interface ControlPreviewRow {
  id: string;
  floor_plan_id: string;
  room_id: string | null;
  product_id: string | null;
  custom_name: string | null;
  name: string | null;
  type: string | null;
  category_info: {
    category?: string | null;
    subcategory?: string | null;
  } | null;
}

interface LvProductPreviewRow {
  id: string;
  floor_plan_id: string;
  room_id: string | null;
  product_id: string | null;
  name: string | null;
  category: string | null;
  subcategory: string | null;
}

interface DesignEntity {
  id: string;
  floor_plan_id: string;
  room_id: string | null;
  product_id: string | null;
  entityType: PortalOutboundEntityType;
  fallbackName: string | null;
  category: string | null;
  subcategory: string | null;
}

function compactText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function productSnapshot(
  product: ProductLibraryRow | undefined,
): PortalOutboundProductSnapshot | null {
  if (!product) return null;

  return {
    id: product.id,
    brand: compactText(product.brand),
    model: compactText(product.model),
    partNumber: compactText(product.part_number),
    description: compactText(product.description) || compactText(product.short_description),
    category: compactText(product.category),
    subcategory: compactText(product.subcategory),
    price: toNumber(product.price),
    sellPrice: toNumber(product.sell_price),
    cost: toNumber(product.cost),
    portalItemId: toNumber(product.portal_item_id),
    referencedItemId: toNumber(product.referenced_item_id),
    portalItemIdentifier: compactText(product.portal_item_identifier),
    source: compactText(product.source),
  };
}

function getDisplayName(
  entity: DesignEntity,
  product: PortalOutboundProductSnapshot | null,
): string {
  const productName = [product?.brand, product?.model].filter(Boolean).join(' ').trim();
  return (
    compactText(productName) ||
    compactText(product?.description) ||
    compactText(entity.fallbackName) ||
    'Unnamed product'
  );
}

export function classifyPortalOutboundItemForPreview(
  entity: DesignEntity,
  product: PortalOutboundProductSnapshot | null,
): { status: PortalOutboundItemStatus; reason: string } {
  if (product?.referencedItemId || product?.portalItemId || product?.portalItemIdentifier) {
    return {
      status: 'exportable',
      reason: product.referencedItemId
        ? 'Matched to Portal referenced item ID'
        : 'Matched to stored Portal item identity',
    };
  }

  if (product && (product.brand || product.model || product.description)) {
    return {
      status: 'ai_fallback',
      reason: 'No Portal item identity; product details can be sent to AI Builder',
    };
  }

  if (
    compactText(entity.fallbackName) &&
    (compactText(entity.category) || compactText(entity.subcategory))
  ) {
    return {
      status: 'ai_fallback',
      reason: 'No linked product; placed item has enough label/category context for AI Builder',
    };
  }

  return {
    status: 'blocked',
    reason: 'Missing Portal identity and insufficient product details',
  };
}

function buildProductKey(
  status: PortalOutboundItemStatus,
  entity: DesignEntity,
  product: PortalOutboundProductSnapshot | null,
): string {
  const portalIdentity =
    product?.referencedItemId || product?.portalItemId || product?.portalItemIdentifier;

  if (portalIdentity) {
    return `${status}:portal:${portalIdentity}`;
  }

  if (product?.id) {
    return `${status}:product:${product.id}`;
  }

  return `${status}:entity:${entity.entityType}:${entity.id}`;
}

function makeAreaKey(entity: DesignEntity, floorPlan: FloorPlanRow, room?: RoomRow): string {
  return room ? `room:${room.id}` : `floor-plan:${floorPlan.id}`;
}

function makeAreaName(floorPlan: FloorPlanRow, room?: RoomRow): string {
  return room?.name || floorPlan.name;
}

async function fetchDesignEntities(floorPlanIds: string[]): Promise<DesignEntity[]> {
  const supabase = await getAuthenticatedSupabaseClient();

  const [fixturesResult, controlsResult, productsResult] = await Promise.all([
    supabase
      .from('lighting_fixtures')
      .select(
        'id, floor_plan_id, room_id, product_id, custom_name, fixture_type, category, subcategory',
      )
      .in('floor_plan_id', floorPlanIds),
    supabase
      .from('lighting_controls')
      .select('id, floor_plan_id, room_id, product_id, custom_name, name, type, category_info')
      .in('floor_plan_id', floorPlanIds),
    supabase
      .from('lv_products')
      .select('id, floor_plan_id, room_id, product_id, name, category, subcategory')
      .in('floor_plan_id', floorPlanIds),
  ]);

  if (fixturesResult.error) throw fixturesResult.error;
  if (controlsResult.error) throw controlsResult.error;
  if (productsResult.error) throw productsResult.error;

  const fixtureRows = (fixturesResult.data || []) as FixturePreviewRow[];
  const controlRows = (controlsResult.data || []) as ControlPreviewRow[];
  const lvProductRows = (productsResult.data || []) as LvProductPreviewRow[];

  const fixtures = fixtureRows.map(
    (fixture): DesignEntity => ({
      id: fixture.id,
      floor_plan_id: fixture.floor_plan_id,
      room_id: fixture.room_id,
      product_id: fixture.product_id,
      entityType: 'fixture',
      fallbackName: fixture.custom_name || fixture.fixture_type,
      category: fixture.category || 'fixture',
      subcategory: fixture.subcategory || fixture.fixture_type,
    }),
  );

  const controls = controlRows.map(
    (control): DesignEntity => ({
      id: control.id,
      floor_plan_id: control.floor_plan_id,
      room_id: control.room_id,
      product_id: control.product_id,
      entityType: 'control',
      fallbackName: control.custom_name || control.name || control.type,
      category: control.category_info?.category || 'control',
      subcategory: control.category_info?.subcategory || control.type,
    }),
  );

  const products = lvProductRows.map(
    (product): DesignEntity => ({
      id: product.id,
      floor_plan_id: product.floor_plan_id,
      room_id: product.room_id,
      product_id: product.product_id,
      entityType: 'lv_product',
      fallbackName: product.name,
      category: product.category,
      subcategory: product.subcategory,
    }),
  );

  return [...fixtures, ...controls, ...products];
}

export class PortalOutboundPreviewService {
  async buildPreview(sourceFloorPlanId: string): Promise<PortalOutboundPreview> {
    const supabase = await getAuthenticatedSupabaseClient();

    const { data: sourceFloorPlan, error: floorPlanError } = await supabase
      .from('floor_plans')
      .select('id, name, project_id')
      .eq('id', sourceFloorPlanId)
      .single();

    if (floorPlanError || !sourceFloorPlan) {
      logger.error('Failed to load source floor plan for Portal outbound preview', floorPlanError);
      throw floorPlanError || new Error('Floor plan not found');
    }

    const floorPlan = sourceFloorPlan as FloorPlanRow;

    const [{ data: projectData, error: projectError }, { data: floorPlanRows, error: plansError }] =
      await Promise.all([
        supabase
          .from('projects')
          .select('id, name, client, location, description')
          .eq('id', floorPlan.project_id)
          .single(),
        supabase
          .from('floor_plans')
          .select('id, name, project_id')
          .eq('project_id', floorPlan.project_id)
          .order('created_at', { ascending: true }),
      ]);

    if (projectError || !projectData) throw projectError || new Error('Project not found');
    if (plansError) throw plansError;

    const project = projectData as ProjectRow;
    const floorPlans = (floorPlanRows || []) as FloorPlanRow[];
    const floorPlanIds = floorPlans.map(plan => plan.id);

    const [{ data: roomRows, error: roomsError }, entities] = await Promise.all([
      supabase.from('rooms').select('id, floor_plan_id, name').in('floor_plan_id', floorPlanIds),
      fetchDesignEntities(floorPlanIds),
    ]);

    if (roomsError) throw roomsError;

    const roomsById = new Map(((roomRows || []) as RoomRow[]).map(room => [room.id, room]));
    const floorPlansById = new Map(floorPlans.map(plan => [plan.id, plan]));
    const productIds = Array.from(
      new Set(entities.map(entity => entity.product_id).filter((id): id is string => Boolean(id))),
    );

    const productRows =
      productIds.length > 0
        ? await supabase
            .from('product_library')
            .select(
              'id, brand, model, part_number, description, short_description, category, subcategory, price, sell_price, cost, portal_item_id, referenced_item_id, portal_item_identifier, source',
            )
            .in('id', productIds)
        : { data: [], error: null };

    if (productRows.error) throw productRows.error;

    const productsById = new Map(
      ((productRows.data || []) as ProductLibraryRow[]).map(product => [product.id, product]),
    );

    const areas = new Map<string, PortalOutboundAreaSummary>();
    const groupedItems = new Map<string, PortalOutboundPreviewItem>();

    for (const entity of entities) {
      const plan = floorPlansById.get(entity.floor_plan_id);
      if (!plan) continue;

      const room = entity.room_id ? roomsById.get(entity.room_id) : undefined;
      const areaKey = makeAreaKey(entity, plan, room);
      const areaName = makeAreaName(plan, room);

      if (!areas.has(areaKey)) {
        areas.set(areaKey, {
          key: areaKey,
          name: areaName,
          floorPlanId: plan.id,
          floorPlanName: plan.name,
          roomId: room?.id || null,
          itemCount: 0,
        });
      }

      const product = productSnapshot(
        entity.product_id ? productsById.get(entity.product_id) : undefined,
      );
      const classification = classifyPortalOutboundItemForPreview(entity, product);
      const productKey = buildProductKey(classification.status, entity, product);
      const itemKey = `${areaKey}:${productKey}`;
      const existing = groupedItems.get(itemKey);

      if (existing) {
        existing.quantity += 1;
        existing.entityIds.push(entity.id);
      } else {
        groupedItems.set(itemKey, {
          key: itemKey,
          status: classification.status,
          reason: classification.reason,
          quantity: 1,
          displayName: getDisplayName(entity, product),
          areaKey,
          areaName,
          floorPlanName: plan.name,
          entityType: entity.entityType,
          product,
          entityIds: [entity.id],
        });
      }

      const area = areas.get(areaKey);
      if (area) area.itemCount += 1;
    }

    const items = Array.from(groupedItems.values()).sort((a, b) => {
      const areaSort = a.areaName.localeCompare(b.areaName);
      return areaSort || a.displayName.localeCompare(b.displayName);
    });

    const issues = [
      ...(!compactText(project.client)
        ? [
            {
              severity: 'warning' as const,
              message: 'Project client is empty; Portal contact creation will need more input.',
            },
          ]
        : []),
      ...(!compactText(project.location)
        ? [
            {
              severity: 'warning' as const,
              message:
                'Project location is empty; Portal tax/location assignment will need more input.',
            },
          ]
        : []),
      {
        severity: 'info' as const,
        message:
          'Preview only: Portal records are created only after you choose the create action.',
      },
    ];

    const counts = {
      totalItems: items.reduce((total, item) => total + item.quantity, 0),
      exportable: items
        .filter(item => item.status === 'exportable')
        .reduce((total, item) => total + item.quantity, 0),
      aiFallback: items
        .filter(item => item.status === 'ai_fallback')
        .reduce((total, item) => total + item.quantity, 0),
      blocked: items
        .filter(item => item.status === 'blocked')
        .reduce((total, item) => total + item.quantity, 0),
      areas: areas.size,
      floorPlans: floorPlans.length,
    };

    return {
      generatedAt: new Date().toISOString(),
      sourceFloorPlanId,
      project: {
        id: project.id,
        name: project.name,
        client: compactText(project.client),
        location: compactText(project.location),
        description: compactText(project.description),
      },
      areas: Array.from(areas.values()).sort((a, b) => {
        const floorSort = a.floorPlanName.localeCompare(b.floorPlanName);
        return floorSort || a.name.localeCompare(b.name);
      }),
      items,
      counts,
      issues,
    };
  }
}

export function createPortalOutboundPreviewService() {
  return new PortalOutboundPreviewService();
}
